import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, CircularProgress, TablePagination, Tooltip, Typography } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
    ChangeSetRequestError, ChangeSetRow, ChangeSetStatus, PolicyUpload, UploadState,
    listChangeSets, listUploads, startIngestion,
} from '../../../api/ontologyChangeSetApi';
import { useOntologyFilters } from '../OntologyBrowser';
import {
    STATUS_COLOR, STATUS_LABEL, SEVERITY_COLOR, changeCountsLabel, competingChangeSets, formatAge, worstSeverity,
} from './changeSetReview';
import { LABEL_SX, MONO_SX, Notice, Pill } from './changeSetUi';
import { dsVar, ink } from '../../../theme/tokens';

/** How often uploads are re-read while one is still being processed. */
export const UPLOAD_POLL_MS = 15000;
const UPLOADS_SHOWN = 8;
const ROWS_PER_PAGE = 25;

const STATUS_FILTERS: Array<{ value: string; label: string }> = [
    { value: 'pending_review', label: 'Awaiting review' },
    { value: 'applied', label: 'Applied' },
    { value: 'partially_applied', label: 'Partially applied' },
    { value: 'rejected', label: 'Rejected' },
    { value: 'failed', label: 'Failed' },
    { value: 'all', label: 'All' },
];

const UPLOAD_STATE: Record<UploadState, { label: string; color: string }> = {
    uploaded: { label: 'Not started', color: '#94A3B8' },
    processing: { label: 'Reading document', color: '#60A5FA' },
    pending_review: { label: 'Ready for review', color: '#FFDE59' },
    no_changes: { label: 'No changes', color: '#94A3B8' },
    decided: { label: 'Decided', color: '#4ADE80' },
    failed: { label: 'Failed', color: '#F87171' },
    duplicate: { label: 'Already uploaded', color: '#94A3B8' },
};

export interface ChangeSetsBrowserProps {
    filters: Record<string, string>;
    /** Bumped by the page after an upload starts or a decision lands, to re-read both lists. */
    refreshKey?: number;
    onIngest?: () => void;
}

/**
 * The Policy Updates tab: the signed-in org's uploads, and the queue of change sets those
 * uploads opened. A row opens the review drawer through `?selected=<changeSetId>`, the
 * same addressable-preview convention the rest of the Ontology tabs use.
 */
const ChangeSetsBrowser: React.FC<ChangeSetsBrowserProps> = ({ filters, refreshKey = 0, onIngest }) => {
    const setFilters = useOntologyFilters();
    const status = filters.status || 'pending_review';
    const selectedId = filters.selected || null;
    const page = Math.max(Number(filters.page || 1) - 1, 0);

    const [uploads, setUploads] = useState<PolicyUpload[] | null>(null);
    const [uploadsError, setUploadsError] = useState<string | null>(null);
    const [rows, setRows] = useState<ChangeSetRow[] | null>(null);
    const [rowsError, setRowsError] = useState<string | null>(null);
    const [loadingRows, setLoadingRows] = useState(false);
    const [starting, setStarting] = useState<string | null>(null);

    // ─── Uploads, polled while any is still processing ──────────────────────────

    const uploadsSignature = useRef('');
    const [queueTick, setQueueTick] = useState(0);

    const loadUploads = useCallback(async (signal?: AbortSignal) => {
        try {
            const result = await listUploads({ signal });
            setUploads(result.uploads);
            setUploadsError(result.changeSetsUnavailable ? 'Policy updates could not be reached; progress may be out of date.' : null);
            // When an upload moves on (e.g. reaches review), the queue has moved too.
            const signature = result.uploads.map((u) => `${u.uploadId}:${u.state}`).join('|');
            if (uploadsSignature.current && signature !== uploadsSignature.current) setQueueTick((n) => n + 1);
            uploadsSignature.current = signature;
        } catch (error) {
            if (signal?.aborted) return;
            setUploadsError(error instanceof ChangeSetRequestError ? error.message : 'Your uploads could not be loaded.');
        }
    }, []);

    useEffect(() => {
        const controller = new AbortController();
        loadUploads(controller.signal);
        return () => controller.abort();
    }, [loadUploads, refreshKey]);

    const anyProcessing = (uploads ?? []).some((u) => u.state === 'processing');
    useEffect(() => {
        if (!anyProcessing) return undefined;
        const timer = setInterval(() => loadUploads(), UPLOAD_POLL_MS);
        return () => clearInterval(timer);
    }, [anyProcessing, loadUploads]);

    // ─── The queue ───────────────────────────────────────────────────────────────

    useEffect(() => {
        const controller = new AbortController();
        setLoadingRows(true);
        setRowsError(null);
        listChangeSets(
            { status: status === 'all' ? '' : (status as ChangeSetStatus), limit: 500 },
            { signal: controller.signal },
        )
            .then((result) => setRows(result))
            .catch((error) => {
                if (controller.signal.aborted) return;
                setRowsError(error instanceof ChangeSetRequestError ? error.message : 'Policy updates could not be loaded.');
            })
            .finally(() => { if (!controller.signal.aborted) setLoadingRows(false); });
        return () => controller.abort();
    }, [status, refreshKey, queueTick]);

    const pageRows = useMemo(
        () => (rows ?? []).slice(page * ROWS_PER_PAGE, (page + 1) * ROWS_PER_PAGE),
        [rows, page],
    );

    const open = (changeSetId?: string | null) => {
        if (changeSetId) setFilters({ selected: changeSetId, page: filters.page || '' });
    };

    const startUpload = async (upload: PolicyUpload) => {
        setStarting(upload.uploadId);
        try {
            await startIngestion(upload.uploadId);
            await loadUploads();
        } catch (error) {
            setUploadsError(error instanceof ChangeSetRequestError ? error.message : 'The document could not be submitted.');
        } finally {
            setStarting(null);
        }
    };

    const uploadAction = (upload: PolicyUpload) => {
        const target = upload.state === 'duplicate' ? upload.duplicateOf : upload.changeSetId;
        if (upload.state === 'uploaded') {
            return (
                <Button size="small" onClick={() => startUpload(upload)} disabled={starting === upload.uploadId}
                    sx={{ textTransform: 'none', color: dsVar('accent-fg'), minWidth: 0 }}>
                    {starting === upload.uploadId ? 'Starting…' : 'Start'}
                </Button>
            );
        }
        if (upload.state === 'failed' && onIngest) {
            return (
                <Button size="small" onClick={onIngest} sx={{ textTransform: 'none', color: dsVar('fg-2'), minWidth: 0 }}>
                    Upload again
                </Button>
            );
        }
        if (!target) return null;
        return (
            <Button size="small" onClick={() => open(target)} sx={{ textTransform: 'none', color: dsVar('accent-fg'), minWidth: 0 }}>
                {upload.state === 'pending_review' ? 'Review' : 'View'}
            </Button>
        );
    };

    return (
        <Box>
            {/* ── Your uploads ── */}
            <Box sx={{ mb: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Typography sx={{ ...LABEL_SX, mb: 0 }}>Recent uploads</Typography>
                    <Button size="small" startIcon={<RefreshIcon sx={{ fontSize: 16 }} />} onClick={() => loadUploads()}
                        sx={{ textTransform: 'none', color: dsVar('fg-2') }}>
                        Refresh
                    </Button>
                </Box>
                {uploadsError && <Box sx={{ mb: 1 }}><Notice tone="warning">{uploadsError}</Notice></Box>}
                {uploads === null && !uploadsError && <CircularProgress size={18} sx={{ color: dsVar('accent-fg') }} />}
                {uploads !== null && uploads.length === 0 && (
                    <Typography sx={{ fontSize: '0.8125rem', color: dsVar('fg-muted') }}>
                        No documents uploaded yet. Use <strong>Upload Policy</strong> to read a policy into the library.
                    </Typography>
                )}
                {uploads !== null && uploads.length > 0 && (
                    <Box sx={{ border: `1px solid ${dsVar('line')}`, borderRadius: '8px', backgroundColor: dsVar('surface') }}>
                        {uploads.slice(0, UPLOADS_SHOWN).map((upload, index) => {
                            const state = UPLOAD_STATE[upload.state] ?? UPLOAD_STATE.processing;
                            const title = upload.changeSet?.title || upload.fileName;
                            const decidedStatus = upload.state === 'decided' && upload.changeSet?.status;
                            return (
                                <Box key={upload.uploadId} data-testid="upload-row" sx={{
                                    display: 'flex', alignItems: 'center', gap: 2, px: 2, py: 1.25,
                                    borderTop: index === 0 ? 'none' : `1px solid ${dsVar('line')}`,
                                }}
                                >
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography sx={{ fontSize: '0.875rem', color: dsVar('fg-body'), overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {title}
                                        </Typography>
                                        <Typography sx={{ fontSize: '0.75rem', color: dsVar('fg-muted') }}>
                                            {upload.fileName !== title && <Box component="span" sx={{ ...MONO_SX, mr: 1 }}>{upload.fileName}</Box>}
                                            {formatAge(upload.submittedAt || upload.createdAt)}
                                            {upload.state === 'failed' && upload.failureReason && (
                                                <Box component="span" sx={{ color: ink('#FCA5A5'), ml: 1 }}>{upload.failureReason}</Box>
                                            )}
                                        </Typography>
                                    </Box>
                                    {upload.state === 'processing' && <CircularProgress size={14} sx={{ color: ink(state.color) }} />}
                                    <Pill color={decidedStatus ? STATUS_COLOR[decidedStatus] : state.color}>
                                        {decidedStatus ? STATUS_LABEL[decidedStatus] : state.label}
                                    </Pill>
                                    <Box sx={{ width: 96, textAlign: 'right' }}>{uploadAction(upload)}</Box>
                                </Box>
                            );
                        })}
                    </Box>
                )}
            </Box>

            {/* ── The queue ── */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
                <Typography sx={{ ...LABEL_SX, mb: 0, mr: 1 }}>Updates</Typography>
                {STATUS_FILTERS.map((option) => {
                    const active = status === option.value;
                    return (
                        <Button
                            key={option.value}
                            size="small"
                            onClick={() => setFilters({ status: option.value === 'pending_review' ? '' : option.value })}
                            sx={{
                                textTransform: 'none', fontSize: '0.75rem', borderRadius: '999px', px: 1.5, py: 0.25, minWidth: 0,
                                color: active ? dsVar('on-accent') : dsVar('fg-2'),
                                backgroundColor: active ? dsVar('accent') : 'transparent',
                                border: `1px solid ${active ? dsVar('accent-fg') : dsVar('line')}`,
                                '&:hover': { backgroundColor: active ? '#F5D149' : dsVar('raised') },
                            }}
                        >
                            {option.label}
                        </Button>
                    );
                })}
            </Box>

            {rowsError && <Box sx={{ mb: 1.5 }}><Notice tone="error">{rowsError}</Notice></Box>}

            <Box sx={{
                border: `1px solid ${dsVar('line')}`, borderRadius: '8px', backgroundColor: dsVar('surface'), overflowX: 'auto',
                opacity: loadingRows && rows ? 0.45 : 1, transition: 'opacity 120ms ease-out',
            }}
            >
                <Box component="table" sx={{ width: '100%', minWidth: 720, borderCollapse: 'collapse' }}>
                    <Box component="thead">
                        <Box component="tr" sx={{ borderBottom: `1px solid ${dsVar('line')}` }}>
                            {['Document', 'Kind', 'Changes', 'Cases affected', 'Status', 'Waiting since'].map((label) => (
                                <Box key={label} component="th" sx={{
                                    textAlign: 'left', px: 2, py: 1.5, fontSize: '0.6875rem', fontWeight: 600,
                                    letterSpacing: '0.05em', textTransform: 'uppercase', color: dsVar('fg-muted'),
                                }}
                                >
                                    {label}
                                </Box>
                            ))}
                        </Box>
                    </Box>
                    <Box component="tbody">
                        {rows === null && (
                            <Box component="tr"><Box component="td" colSpan={6} sx={{ p: 6, textAlign: 'center' }}>
                                <CircularProgress size={24} sx={{ color: dsVar('accent-fg') }} />
                            </Box></Box>
                        )}
                        {rows !== null && rows.length === 0 && (
                            <Box component="tr"><Box component="td" colSpan={6} sx={{ p: 6, textAlign: 'center', color: dsVar('fg-muted'), fontSize: '0.875rem' }}>
                                {status === 'pending_review' ? 'Nothing is awaiting review.' : 'No updates match this filter.'}
                            </Box></Box>
                        )}
                        {pageRows.map((row, index) => {
                            const runtime = row.impact?.runtime;
                            const worst = worstSeverity(runtime?.summary?.bySeverity);
                            const competing = competingChangeSets(row, rows ?? []);
                            const isSelected = row.changeSetId === selectedId;
                            return (
                                <Box
                                    key={row.changeSetId}
                                    component="tr"
                                    data-testid="change-set-row"
                                    onClick={() => open(row.changeSetId)}
                                    sx={{
                                        borderBottom: index === pageRows.length - 1 ? 'none' : `1px solid ${dsVar('line')}`,
                                        cursor: 'pointer',
                                        backgroundColor: isSelected ? dsVar('raised') : 'transparent',
                                        boxShadow: isSelected ? `inset 2px 0 0 ${dsVar('accent-fg')}` : 'none',
                                        '&:hover': { backgroundColor: dsVar('raised') },
                                        '& td': { px: 2, py: 1.75, fontSize: '0.875rem', color: dsVar('fg-soft'), verticalAlign: 'top' },
                                    }}
                                >
                                    <Box component="td">
                                        <Typography sx={{ fontSize: '0.875rem', color: dsVar('fg-body'), fontWeight: 500 }}>
                                            {row.sourceDocument?.title || row.sourceDocCode || 'Untitled document'}
                                            {row.sourceDocument?.versionLabel && (
                                                <Box component="span" sx={{ color: dsVar('fg-muted'), ml: 1 }}>v{row.sourceDocument.versionLabel}</Box>
                                            )}
                                        </Typography>
                                        {competing.length > 0 && (
                                            <Tooltip title="Another change set for the same document is also awaiting review. Approving both would apply competing versions.">
                                                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, mt: 0.5, color: ink('#FBBF24'), fontSize: '0.75rem' }}>
                                                    <WarningAmberIcon sx={{ fontSize: 14 }} /> {competing.length} competing
                                                </Box>
                                            </Tooltip>
                                        )}
                                    </Box>
                                    <Box component="td">
                                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                            <Pill color={row.baseline ? '#60A5FA' : '#4ADE80'}>{row.baseline ? 'Revision' : 'New'}</Pill>
                                            {row.scopeLevel === 'regulatory' && <Pill color="#C4B5FD">Regulatory</Pill>}
                                        </Box>
                                    </Box>
                                    <Box component="td">{changeCountsLabel(row.summary)}</Box>
                                    <Box component="td">
                                        {runtime?.unavailable ? (
                                            <Typography sx={{ fontSize: '0.8125rem', color: dsVar('fg-muted') }}>Unavailable</Typography>
                                        ) : (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                {runtime?.summary?.customers ?? 0}
                                                {worst && <Pill color={SEVERITY_COLOR[worst]}>{worst}</Pill>}
                                            </Box>
                                        )}
                                    </Box>
                                    <Box component="td">
                                        <Pill color={STATUS_COLOR[row.status] ?? '#94A3B8'}>{STATUS_LABEL[row.status] ?? row.status}</Pill>
                                    </Box>
                                    <Box component="td" sx={{ whiteSpace: 'nowrap' }}>
                                        {row.status === 'pending_review' ? formatAge(row.reviewRequestedAt) : formatAge(row.createdAt)}
                                    </Box>
                                </Box>
                            );
                        })}
                    </Box>
                </Box>
            </Box>

            {(rows?.length ?? 0) > ROWS_PER_PAGE && (
                <TablePagination
                    component="div"
                    count={rows?.length ?? 0}
                    page={page}
                    rowsPerPage={ROWS_PER_PAGE}
                    rowsPerPageOptions={[ROWS_PER_PAGE]}
                    onPageChange={(_e, next) => setFilters({ page: String(next + 1) })}
                    sx={{ color: dsVar('fg-2'), '& .MuiIconButton-root': { color: dsVar('fg-2') } }}
                />
            )}
        </Box>
    );
};

export default ChangeSetsBrowser;
